// Text-anchoring for Review Mode. Annotations are anchored to character offsets
// in a plain-text "read model" of the scene (the same concatenated-text-node
// model the TTS read-along uses), plus the selected text and the text just
// before/after it. That lets an annotation be relocated when the prose shifts —
// or flagged orphaned when it genuinely can't be found — rather than silently
// attaching to the wrong text. Nothing here mutates the DOM.

import type { ReviewAnchor } from "../lib/types.js";

export interface TextSpan {
  node: Text | null; // null = a block-break gap that maps to no node
  start: number;
  end: number;
}

export interface ReadModel {
  text: string;
  spans: TextSpan[];
}

const CONTEXT_LEN = 40;

function nearestBlock(node: Node, root: HTMLElement): Element | null {
  let el: Node | null = node.parentNode;
  while (el && el !== root) {
    if (el instanceof HTMLElement) {
      const d = getComputedStyle(el).display;
      if (d === "block" || d === "list-item" || d === "flex" || d === "grid") return el;
    }
    el = el.parentNode;
  }
  return root;
}

/** Concatenate the container's text nodes (with block-break gaps) + a span map. */
export function buildReadModel(root: HTMLElement): ReadModel {
  const spans: TextSpan[] = [];
  let text = "";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let lastBlock: Element | null = null;
  let node = walker.nextNode();
  while (node) {
    const tn = node as Text;
    const content = tn.textContent ?? "";
    if (content.length > 0) {
      const block = nearestBlock(tn, root);
      if (lastBlock && block !== lastBlock && text.length > 0) {
        const sepStart = text.length;
        text += "\n\n";
        spans.push({ node: null, start: sepStart, end: text.length });
      }
      lastBlock = block;
      const start = text.length;
      text += content;
      spans.push({ node: tn, start, end: text.length });
    }
    node = walker.nextNode();
  }
  return { text, spans };
}

/** Map a DOM position (node + offset) to an offset in the read model. */
function offsetOf(model: ReadModel, node: Node, offset: number): number | null {
  for (const span of model.spans) {
    if (span.node === node) return span.start + Math.min(offset, span.node.length);
  }
  // The position may be an element boundary; fall back to the nearest text node.
  if (node.nodeType === Node.ELEMENT_NODE) {
    const child = node.childNodes[Math.min(offset, node.childNodes.length - 1)];
    if (child) {
      for (const span of model.spans) {
        if (span.node && (span.node === child || child.contains(span.node))) return span.start;
      }
    }
  }
  return null;
}

/** Turn a [start,end) model span into a DOM Range (skipping block-break gaps). */
export function rangeFromOffsets(model: ReadModel, start: number, end: number): Range | null {
  let startNode: Text | null = null;
  let startOffset = 0;
  let endNode: Text | null = null;
  let endOffset = 0;
  for (const span of model.spans) {
    if (!span.node) continue;
    if (startNode === null && end > span.start && start < span.end) {
      startNode = span.node;
      startOffset = Math.max(0, start - span.start);
    }
    if (start < span.end && end > span.start) {
      endNode = span.node;
      endOffset = Math.min(span.node.length, end - span.start);
    }
  }
  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, Math.min(startOffset, startNode.length));
    range.setEnd(endNode, Math.min(endOffset, endNode.length));
    return range;
  } catch {
    return null;
  }
}

export interface BuiltAnchor {
  anchor: ReviewAnchor;
  selectedText: string;
}

/** Build a text anchor from the current selection inside `root`, or null. */
export function anchorFromSelection(root: HTMLElement, selection: Selection): BuiltAnchor | null {
  if (selection.rangeCount === 0 || selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const model = buildReadModel(root);
  const start = offsetOf(model, range.startContainer, range.startOffset);
  const end = offsetOf(model, range.endContainer, range.endOffset);
  if (start === null || end === null || end <= start) return null;
  const selectedText = model.text.slice(start, end);
  if (!selectedText.trim()) return null;
  return {
    selectedText,
    anchor: {
      start,
      end,
      prefix: model.text.slice(Math.max(0, start - CONTEXT_LEN), start),
      suffix: model.text.slice(end, Math.min(model.text.length, end + CONTEXT_LEN))
    }
  };
}

export interface ResolvedAnchor {
  range: Range | null;
  orphaned: boolean;
}

/**
 * Resolve a stored anchor against the current read model.
 * 1. Exact offsets still match the selected text → use them.
 * 2. Otherwise relocate by selected text, disambiguated by prefix/suffix.
 * 3. If it can't be found confidently → orphaned (never guess wrong).
 */
export function resolveAnchor(model: ReadModel, anchor: ReviewAnchor | null, selectedText: string | null): ResolvedAnchor {
  if (!anchor || anchor.start === undefined || anchor.end === undefined || !selectedText) {
    return { range: null, orphaned: false };
  }
  const { text } = model;

  // 1. Exact offsets unchanged.
  if (text.slice(anchor.start, anchor.end) === selectedText) {
    return { range: rangeFromOffsets(model, anchor.start, anchor.end), orphaned: false };
  }

  // 2. Relocate by content. Collect every occurrence of the selected text.
  const occurrences: number[] = [];
  let i = text.indexOf(selectedText);
  while (i !== -1 && occurrences.length < 50) {
    occurrences.push(i);
    i = text.indexOf(selectedText, i + 1);
  }
  if (occurrences.length === 0) return { range: null, orphaned: true };
  if (occurrences.length === 1) {
    const s = occurrences[0]!;
    return { range: rangeFromOffsets(model, s, s + selectedText.length), orphaned: false };
  }

  // Disambiguate by surrounding context (prefix/suffix).
  const prefix = anchor.prefix ?? "";
  const suffix = anchor.suffix ?? "";
  let best = -1;
  let bestScore = -1;
  for (const occ of occurrences) {
    const before = text.slice(Math.max(0, occ - prefix.length), occ);
    const after = text.slice(occ + selectedText.length, occ + selectedText.length + suffix.length);
    let score = 0;
    if (prefix && before.endsWith(prefix)) score += 2;
    else if (prefix && before.slice(-10) === prefix.slice(-10)) score += 1;
    if (suffix && after.startsWith(suffix)) score += 2;
    else if (suffix && after.slice(0, 10) === suffix.slice(0, 10)) score += 1;
    // Nearest-to-original tie-breaker.
    if (score === bestScore && best !== -1 && anchor.start !== undefined) {
      if (Math.abs(occ - anchor.start) < Math.abs(best - anchor.start)) best = occ;
    } else if (score > bestScore) {
      bestScore = score;
      best = occ;
    }
  }
  // Ambiguous with no contextual signal → don't guess.
  if (best === -1 || bestScore <= 0) return { range: null, orphaned: true };
  return { range: rangeFromOffsets(model, best, best + selectedText.length), orphaned: false };
}
